// eslint-disable-next-line eag/no-external-date-time-format-functions -- Custom format
import {format} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';
import {Attributes, AttributesRow, Card} from 'platform/components';
import {Heading, HStack, Link, Show, Space, Stack, Text} from 'platform/foundation';
import {NumberFormat, useFormatNumber} from 'platform/locale';

import {useParams} from 'react-router-dom';

import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {buildArray, parseDate, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {ELECTRIC_TYPES} from '../../../../constants/electricTypes';
import {CatalogueType} from '../../../../hooks/useCatalogue';
import {useVehicleTranslatedInfo} from '../../../../hooks/useVehicleTranslatedInfo';
import i18n from '../../../../i18n/i18n';
import {GetRequestApiResponse} from '../../../../types/api/api';
import {PriceReportType} from '../../../../types/PriceReportType';
import {featureFlags} from '../../../../utils/featureFlags';
import {getCreatePath} from '../../../../utils/routing';
import {useIsPrintForced} from '../../hooks/useIsPrintForced';
import {VehicleBodyColorDot} from './components/VehicleBodyColorDot';

interface VehicleParametersCardProps extends TestIdProps {
  priceReport: PriceReportType;
  request: GetRequestApiResponse;
  catalogue: CatalogueType;
}

export function VehicleParametersCard(props: VehicleParametersCardProps) {
  const {isPrint} = useIsPrintForced();
  const formatNumber = useFormatNumber();
  const navigate = useNavigate();
  const params = useParams();

  const {data} = useVehicleTranslatedInfo(props.priceReport.vehicle);

  const vehicle = props.priceReport.vehicle;
  const isEditable =
    isFeatureEnabled(featureFlags.PR_EDIT) && !!props.request?.additionalSettings?.editable;

  const firstRegistration = isNotNil(vehicle.firstRegistrationOn)
    ? format(parseDate(vehicle.firstRegistrationOn), 'MM/yyyy')
    : vehicle.firstRegistrationOnYear.toString();

  const bodyColor = props.catalogue.getBodyColorHEX(vehicle.exteriorColor);

  const attributes = buildArray<AttributesRow>()
    .when(isNotNilOrEmpty(vehicle.vin), {
      label: i18n.t('entity.overviewParameters.labels.vin'),
      content: (
        <Text size="xSmall" alternative>
          {vehicle.vin}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.make'),
      content: (
        <Text size="xSmall" alternative>
          {data.make}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.model'),
      content: (
        <Text size="xSmall" alternative>
          {data.model}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.trimLevel), {
      label: i18n.t('entity.overviewParameters.labels.trimLevel'),
      content: (
        <Text size="xSmall" alternative>
          {data.trimLevel}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.bodyStyle'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getCarStyle(vehicle.bodyStyle)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.exteriorColor), {
      label: i18n.t('entity.overviewParameters.labels.exteriorColor'),
      content: (
        <HStack spacing={2} align="center">
          <Text size="xSmall" alternative>
            {props.catalogue.getBodyColor(vehicle.exteriorColor)}
          </Text>
          {isNotNil(bodyColor) && <VehicleBodyColorDot $backgroundColor={bodyColor} />}
        </HStack>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.exteriorColorType), {
      label: i18n.t('entity.overviewParameters.labels.exteriorColorType'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getColorType(vehicle.exteriorColorType)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.doorCountCategory), {
      label: i18n.t('entity.overviewParameters.labels.doorCount'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getDoorCount(vehicle.doorCountCategory)}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.transmission'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getTransmission(vehicle.transmission)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.seatCount), {
      label: i18n.t('entity.overviewParameters.labels.seatCount'),
      content: (
        <Text size="xSmall" alternative>
          {vehicle.seatCount}
        </Text>
      ),
    })
    .whenNot(
      ELECTRIC_TYPES.includes(vehicle.fuelType) &&
        isNotNil(vehicle.maxPower) &&
        isFeatureEnabled(featureFlags.PR_SPLIT_POWER),
      {
        label: i18n.t('entity.overviewParameters.labels.power'),
        content: (
          <Text
            size="xSmall"
            alternative
          >{`${formatNumber(vehicle.maxPower ?? vehicle.power)} ${i18n.t(
            'general.metric.kW'
          )}`}</Text>
        ),
      }
    )
    .when(
      ELECTRIC_TYPES.includes(vehicle.fuelType) &&
        isNotNil(vehicle.maxPower) &&
        isFeatureEnabled(featureFlags.PR_SPLIT_POWER),
      {
        label: i18n.t('entity.overviewParameters.labels.powerAndMaxPower'),
        content: (
          <Text size="xSmall" alternative>{`${formatNumber(vehicle.power)} ${i18n.t(
            'general.metric.kW'
          )} / ${formatNumber(vehicle.maxPower)} ${i18n.t('general.metric.kW')}`}</Text>
        ),
      }
    )
    .add({
      label: i18n.t('entity.overviewParameters.labels.drive'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getDrive(vehicle.drive)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.gearCount), {
      label: i18n.t('entity.overviewParameters.labels.gearCount'),
      content: (
        <Text size="xSmall" alternative>
          {vehicle.gearCount}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.engineVolume), {
      label: i18n.t('entity.overviewParameters.labels.engineVolume'),
      content: (
        <Text size="xSmall" alternative>
          <NumberFormat number={vehicle.engineVolume} decimals={0} />
          &nbsp;
          {i18n.t('general.metric.ccm')}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.fuelType), {
      label: i18n.t('entity.overviewParameters.labels.fuelType'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getFuelType(vehicle.fuelType)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.carbonDioxideEmission), {
      label: i18n.t('entity.overviewParameters.labels.carbonDioxideEmission'),
      content: (
        <Text size="xSmall" alternative>
          <NumberFormat number={Number(vehicle.carbonDioxideEmission)} decimals={0} />
          &nbsp;
          {i18n.t('general.metric.gPerKm')}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.emissionClass), {
      label: i18n.t('entity.overviewParameters.labels.emissionClass'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getEmissionClass(vehicle.emissionClass)}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.operatingWeight), {
      label: i18n.t('entity.overviewParameters.labels.weight'),
      content: (
        <Text size="xSmall" alternative>
          <NumberFormat number={vehicle.operatingWeight} />
          &nbsp;
          {i18n.t('general.metric.kg')}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.mileage'),
      content: (
        <Text size="xSmall" alternative>{`${formatNumber(vehicle.mileage)} ${i18n.t(
          'general.metric.km'
        )}`}</Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.firstRegistration'),
      content: (
        <Text size="xSmall" alternative>
          {firstRegistration}
        </Text>
      ),
    })
    .when(isNotNilOrEmpty(vehicle.interiorMaterial), {
      label: i18n.t('entity.overviewParameters.labels.interiorMaterial'),
      content: (
        <Text size="xSmall" alternative>
          {props.catalogue.getInteriorMaterial(vehicle.interiorMaterial)}
        </Text>
      ),
    })
    .add({
      label: i18n.t('entity.overviewParameters.labels.isVatDeductible'),
      content: (
        <Text size="xSmall" alternative>
          {vehicle.isVatDeductible ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')}
        </Text>
      ),
    });

  return (
    <Card variant={isPrint ? 'inlineWhite' : undefined} data-testid={props['data-testid']}>
      <Space vertical={2} />
      <Stack
        direction={['column', 'column', 'row']}
        justify={isEditable ? ['center', 'center', 'space-between'] : 'center'}
        align="center"
        spacing={[3, 3, 4]}
      >
        <Heading size={2} data-testid={props['data-testid']}>
          {i18n.t('entity.overviewParameters.labels.vehicleParameters')}
        </Heading>
        <Show when={isEditable}>
          <Link
            title={i18n.t('general.actions.edit')}
            leftIcon="image/edit"
            size="small"
            onClick={() => navigate(getCreatePath(params))}
            data-testid={suffixTestId('editLink', props)}
          />
        </Show>
      </Stack>
      <Space vertical={6} />
      <Attributes rows={attributes} data-testid={suffixTestId('attributes', props)} />
    </Card>
  );
}
